import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import FreshChat from 'react-freshchat'
import { useMsal } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'
import find from 'lodash/find'

import { GlobalContext } from '../../App'
import { ContextEntity } from '../../customEntites/context.entity'
import { ContractEntity, UserEntity } from '../../entities'
import { userApi } from '../../api/user'
import { api } from '../../services/api.service'
import LoadingOverlay from '../LoadingOverlay'
import BackIcon from '../../assets/icons/back'
import FreshChatIcon from '../../assets/icons/freshchat'
import './index.scss'

interface PropertyManager {
  partner_email?: string
}

interface SelectedContract {
  address: string
  contractNumber: string
}

const splitContactName = (fullName?: string): { firstName: string; lastName: string } => {
  const name = (fullName || '').trim()
  if (!name) {
    return { firstName: '', lastName: '' }
  }
  const parts = name.split(/\s+/)
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  }
}

const ContractCard = ({
  contract,
  onSelect
}: {
  contract: ContractEntity
  onSelect: () => void
}) => {
  const isPending = contract.approvalState !== 'approved'
  return (
    <a
      href='/'
      className='chat-contract-card'
      onClick={(e) => {
        e.preventDefault()
        onSelect()
      }}
      style={{
        textDecoration: 'none',
        color: 'unset',
        textAlign: 'left'
      }}
    >
      <div>
        <p className='number'>
          {contract.contractNumber}
          {isPending && <span className='contract-status-pending'> · Pending</span>}
        </p>
        <p>{contract.address}</p>
      </div>
    </a>
  )
}

const Freshchat = () => {
  const { t, i18n } = useTranslation()
  const { accounts } = useMsal()
  const { context }: ContextEntity = useContext(GlobalContext)

  const [user, setUser] = useState<UserEntity | null>(null)
  const [agents, setAgents] = useState('')
  const [settingContract, setSettingContract] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedContract, setSelectedContract] = useState<SelectedContract>({
    address: '',
    contractNumber: ''
  })

  const closeChatRef = useRef<() => void>(() => undefined)
  const closeSelectContractRef = useRef<() => void>(() => undefined)

  const activeContracts = useMemo(
    () => context?.contracts?.activeContracts ?? [],
    [context?.contracts?.activeContracts]
  )

  // All contracts (approved + pending) used for the chat contract selector,
  // matching tenant app behaviour where users can pick any contract to chat about
  const allContracts = useMemo(
    () => context?.contracts?.contracts ?? [],
    [context?.contracts?.contracts]
  )

  const externalId = accounts[0]?.localAccountId ?? ''
  const locale = (i18n.language || 'de').toLowerCase().slice(0, 2)
  const freshchatHost = process.env.REACT_APP_FRESHCHAT_HOST || 'https://wchat.eu.freshchat.com'
  const freshchatToken = process.env.REACT_APP_FRESHCHAT_TOKEN || ''
  const freshchatWidgetUuid = process.env.REACT_APP_FRESHCHAT_WIDGET_UUID || ''
  const freshchatAppLogo = process.env.REACT_APP_FRESHCHAT_APP_LOGO || ''
  const freshchatAppName =
    process.env.REACT_APP_FRESHCHAT_APP_NAME || 'ATCP'

  useEffect(() => {
    userApi
      .getUserProfile()
      .then(setUser)
      .catch(() => setUser(null))
  }, [])

  const fetchPropertyManagers = async (contractNumber: string): Promise<string> => {
    try {
      const res = await api.get<PropertyManager[]>(
        `/api/contract/${contractNumber}/property-managers`
      )
      const emails = (res.data || [])
        .map((agent) => agent.partner_email)
        .filter(Boolean)
        .join(',')
      return emails || 'none'
    } catch {
      return 'none'
    }
  }

  const showChat = useCallback(
    async (contractNumber?: string) => {
      setLoading(true)

      if (contractNumber) {
        const contract = find(activeContracts, { contractNumber }) as ContractEntity | undefined
        if (contract) {
          setSelectedContract({
            address: contract.address,
            contractNumber: contract.contractNumber
          })
        }
        const propertyManagers = await fetchPropertyManagers(contractNumber)
        setAgents(propertyManagers)
      } else {
        setAgents('none')
      }
      setLoading(false)
    },
    [activeContracts]
  )

  const closeChat = useCallback(() => {
    window.fcWidget?.destroy()
    setSettingContract(false)
    setAgents('')
  }, [])

  const closeSelectContract = useCallback(() => {
    setLoading(false)
    setSettingContract(false)
  }, [])

  closeChatRef.current = closeChat
  closeSelectContractRef.current = closeSelectContract

  const toggleOpenChat = (e?: React.MouseEvent) => {
    e?.stopPropagation()

    if (!agents) {
      if (allContracts.length === 1) {
        showChat(allContracts[0].contractNumber)
      } else if (allContracts.length === 0) {
        showChat()
      }
    }

    if (
      e &&
      (e.target instanceof SVGElement ||
        (e.target as HTMLElement).className === 'close-chat' ||
        (e.target as HTMLElement).className === 'open-chat' ||
        e.target instanceof SVGPathElement)
    ) {
      setSettingContract((prev) => !prev)
    }
  }

  const userProperties = useMemo(() => {
    const { firstName, lastName } = splitContactName(user?.contactPersonName)
    const businessFallback = splitContactName(user?.businessName)

    return {
      email: user?.email || user?.contactPersonEmail || '',
      firstName: firstName || businessFallback.firstName,
      lastName: lastName || businessFallback.lastName,
      phone: user?.contactPersonPhone || '',
      agents,
      Address: selectedContract.address,
      Contract_number: selectedContract.contractNumber,
      businessName: user?.businessName || ''
    }
  }, [user, agents, selectedContract])

  if (!freshchatToken) {
    return null
  }

  return (
    <div>
      {loading && <LoadingOverlay />}
      {!agents ? (
        <div className='chat-wrap' onClick={(e) => toggleOpenChat(e)}>
          {settingContract ? (
            <button type='button' className='close-chat' onClick={(e) => toggleOpenChat(e)}>
              x
            </button>
          ) : (
            <button
              type='button'
              className='open-chat'
              onClick={(e) => toggleOpenChat(e)}
              aria-label={String(t('FRESHCHAT.OPEN_CHAT'))}
            >
              <FreshChatIcon />
            </button>
          )}

          <div
            className='contract-select-wrap'
            style={{ display: settingContract ? 'block' : 'none' }}
          >
            <div className='contract-select'>
              <header>
                <div className='wrapper'>
                  <div
                    className='back'
                    onClick={(e) => toggleOpenChat(e)}
                    onKeyDown={(e) => e.key === 'Enter' && toggleOpenChat()}
                    role='button'
                    tabIndex={0}
                  >
                    <BackIcon />
                  </div>
                  <div className='title'>{t('FRESHCHAT.SELECT_CONTRACT')}</div>
                  <div style={{ width: '20px' }} />
                </div>
              </header>
              {allContracts.map((contract) => (
                <ContractCard
                  key={contract.appId || contract.contractNumber}
                  contract={contract}
                  onSelect={() => showChat(contract.contractNumber)}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <FreshChat
          token={freshchatToken}
          host={freshchatHost}
          {...({ widgetUuid: freshchatWidgetUuid, siteId: 'at-connect' } as object)}
          externalId={externalId}
          locale={locale}
          restoreId={user?.freshchatConversationId ?? undefined}
          config={{
            agent: {
              hideName: true,
              hidePic: true
            },
            disableNotifications: true
          }}
          onInit={(widget) => {
            widget.user.setProperties(userProperties)

            widget.on('user:created', async (resp: unknown) => {
              try {
                const response = resp as { status?: number; data?: { restoreId?: string } }
                const status = response?.status
                const data = response?.data

                if (status === 200 && data?.restoreId) {
                  await userApi.saveFreshchatConversationId(data.restoreId)
                  setUser((prev) =>
                    prev ? { ...prev, freshchatConversationId: data.restoreId } : prev
                  )
                }
              } catch (err) {
                console.error('get user restore id error:', err)
              }
            })

            if (allContracts.length > 1) {
              widget.on('widget:closed', () => closeChatRef.current())
              widget.on('widget:opened', () => closeSelectContractRef.current())
              widget.open()
            } else {
              closeSelectContractRef.current()
            }
          }}
        />
      )}
    </div>
  )
}

export default Freshchat
